<script setup>
import { ref, computed, nextTick } from 'vue';

const props = defineProps({
  blockdata: Object,
  city: { type: String, default: '' },
});

const expanded = ref(false);
const body = computed(() => props.blockdata?.longdescription || props.blockdata?.usl_desc || '');
// Сворачиваем только заметно длинный текст (по «голому» тексту без тегов)
const isLong = computed(() => body.value.replace(/<[^>]+>/g, '').length > 700);

// Оглавление по H2: собираем пункты и проставляем id для якорей
const processed = computed(() => {
  let i = 0;
  const toc = [];
  const html = (body.value || '').replace(/<h2([^>]*)>([\s\S]*?)<\/h2>/gi, (m, attrs, inner) => {
    const text = inner.replace(/<[^>]+>/g, '').trim();
    if (!text) return m;
    const id = 'sec-' + (++i);
    toc.push({ id, text });
    return `<h2${attrs} id="${id}" style="scroll-margin-top:96px">${inner}</h2>`;
  });
  return { html, toc };
});

function goTo(id) {
  expanded.value = true; // раскрываем свёрнутый текст, чтобы якорь был виден
  nextTick(() => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
}
</script>

<template>
  <section v-if="blockdata && (blockdata.longdescription || blockdata.usl_desc)" class="bg-white">
    <div class="max-w-7xl mx-auto px-4 py-8 md:py-12">
      <div class="rounded-r-2xl border border-gray-100 border-l-4 border-l-brand-600 bg-slate-50 shadow-sm p-6 md:p-8">
          <div class="flex items-start gap-3 mb-4">
            <span class="shrink-0 flex items-center justify-center w-10 h-10 rounded-xl bg-brand-50 text-brand-600">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 3v17.25m0 0c-1.472 0-2.882.265-4.185.75M12 20.25c1.472 0 2.882.265 4.185.75M18.75 4.97A48.416 48.416 0 0 0 12 4.5c-2.291 0-4.545.16-6.75.47m13.5 0c1.01.143 2.01.317 3 .52m-3-.52 2.62 10.726c.122.499-.106 1.028-.589 1.202a5.988 5.988 0 0 1-2.031.352 5.988 5.988 0 0 1-2.031-.352c-.483-.174-.711-.703-.59-1.202L18.75 4.971Zm-16.5.52c.99-.203 1.99-.377 3-.52m0 0 2.62 10.726c.122.499-.106 1.028-.589 1.202a5.989 5.989 0 0 1-2.031.352 5.989 5.989 0 0 1-2.031-.352c-.483-.174-.711-.703-.59-1.202L5.25 4.971Z" />
              </svg>
            </span>
            <h2 class="text-2xl lg:text-3xl font-extrabold text-gray-900 tracking-tight leading-tight">
              {{ blockdata.usl_name }}
            </h2>
          </div>
          <div class="relative">
            <div class="md:flow-root overflow-hidden transition-all duration-300"
              :class="isLong && !expanded ? 'max-h-[28rem]' : ''">
              <!-- Оглавление: на ПК обтекается текстом справа -->
              <nav v-if="processed.toc.length >= 3"
                class="mb-5 rounded-xl bg-white border border-gray-100 p-4 md:float-right md:w-72 md:ml-6 md:mb-3">
                <p class="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2.5">Содержание</p>
                <ol class="space-y-1.5 list-none">
                  <li v-for="(h, i) in processed.toc" :key="h.id">
                    <a :href="'#' + h.id" @click.prevent="goTo(h.id)"
                       class="flex gap-2 text-sm text-indigo-600 hover:text-indigo-800 hover:underline">
                      <span class="text-gray-500 tabular-nums shrink-0">{{ i + 1 }}.</span>
                      <span>{{ h.text }}</span>
                    </a>
                  </li>
                </ol>
              </nav>

              <div
                class="richtext text-gray-600 text-base leading-relaxed
                       [&>h2]:text-gray-900 [&>h2]:font-bold [&>h2]:text-xl [&>h2]:mt-6 [&>h2]:mb-2.5
                       [&>h3]:text-gray-900 [&>h3]:font-semibold [&>h3]:text-lg [&>h3]:mt-5 [&>h3]:mb-2
                       [&>p]:mb-3.5 [&>strong]:text-gray-900
                       [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:mb-3.5 [&_li]:mb-1
                       [&>ol]:list-decimal [&>ol]:pl-6 [&>ol]:mb-3.5"
                v-html="processed.html"
              ></div>
            </div>
            <div
              v-if="isLong && !expanded"
              class="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-slate-50 to-slate-50/0 pointer-events-none"
            ></div>
          </div>

          <button
            v-if="isLong"
            @click="expanded = !expanded"
            class="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-indigo-600 bg-indigo-50 rounded-full px-4 py-1.5 border border-indigo-100 hover:bg-indigo-100 transition-colors"
          >
            {{ expanded ? 'Свернуть' : 'Читать полностью' }}
            <svg class="w-4 h-4 transition-transform duration-300" :class="expanded ? 'rotate-180' : ''" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
            </svg>
          </button>

          <p v-if="city" class="mt-6 text-gray-500 leading-relaxed">
            Юристы «Мина и партнёры», {{ city }}: поможем по теме «{{ blockdata.usl_name }}» — честно оценим перспективы дела и подскажем порядок действий.
          </p>
      </div>
    </div>
  </section>
</template>
